import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Avatar,
	Button,
	Card,
	Dropdown,
	Icon,
	Input,
	Menu,
	message,
	Modal,
	PageHeader,
	Select,
	Switch,
	Table,
	Tag,
	Typography,
} from 'antd';
import * as Yup from 'yup';

import Meta from '../../../../components/Meta';
import Fallback from '../../../../components/Fallback';
import {
	FiltersContainer,
	AvatarCell,
	Container,
	UpdateFormContainer,
	UpdateAvatarContainer,
} from './styles';
import Form from '../../../../components/Form';
import RedactorsAPI from '../../../../services/sdks/redactors';
import { resolveFileSrc } from '../../../../helpers/fileSrcResolver';
import { TbEdit } from 'react-icons/tb';
import env from '../../../../config/env';
import UsersAPI from '../../../../services/sdks/user';

const breadcrumb = {
	routes: [{ breadcrumbName: 'Painel Administrativo' }, { breadcrumbName: 'Jornalistas' }],
	style: { marginBottom: 12 },
};

const DEFAULT_PAGINATION = { currentPage: 0, total: 0 };

const ListRedactors = () => {
	const uploadInputRef = useRef();

	const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
	const [redactors, setRedactors] = useState([]);
	const [fallback, setFallback] = useState({ initialData: true });
	const [handlingRedactor, setHandlingRedactor] = useState(null);
	const [filters, setFilters] = useState({
		name: '',
		email: '',
		isActive: undefined,
	});
	const [visibleModals, setVisibleModals] = useState({
		edit: false,
		changePassword: false,
		create: false,
		updateRadios: false,
	});
	const [password, setPassword] = useState('');
	const [passwordConfirmation, setPasswordConfirmation] = useState('');
	const [avatarFile, setAvatarFile] = useState(null);
	const [radios, setRadios] = useState([]);
	const [selectedRadios, setSelectedRadios] = useState([]);

	const fetchData = useCallback(async (params) => {
		setFallback((prev) => ({ ...prev, fetchingRedactors: true }));

		try {
			const { data } = await RedactorsAPI.listRedactors(params);
			setRedactors(data.redactors);
			setPagination((prev) => ({ ...prev, total: data.total }));
		} catch (error) {
			message.error('Erro ao buscar os dados, tente novamente');
		}

		setFallback((prev) => ({
			...prev,
			fetchingRedactors: false,
			initialData: false,
		}));
	}, []);

	const handleDeleteRedactor = useCallback(
		async (id) => {
			try {
				await RedactorsAPI.deleteRedactor(id);
				message.success('Jornalista excluído com sucesso');
				return fetchData({
					...pagination,
					filters,
				});
			} catch (error) {
				message.error('Erro ao excluir o jornalista, tente novamente');
			}
		},
		[fetchData, filters, pagination]
	);

	const onFilter = useCallback(
		(e) => {
			e.preventDefault();
			setPagination(DEFAULT_PAGINATION);
			fetchData({
				...DEFAULT_PAGINATION,
				filters,
			});
		},
		[fetchData, filters]
	);

	const columns = useMemo(() => {
		return [
			{
				title: 'ID',
				dataIndex: '_id',
				key: 'id',
				render: (_id) => (
					<Typography.Text title={_id} copyable={{ text: _id }}>{`${_id.slice(
						0,
						12
					)}...`}</Typography.Text>
				),
			},
			{
				title: 'Nome',
				key: 'name',
				render: (redactor) => (
					<AvatarCell>
						<Avatar
							style={{ background: 'var(--primary)' }}
							src={resolveFileSrc({
								fileName: redactor?.avatar,
								folder: ['profile_pics'],
								bucket: env.miscS3Bucket,
							})}>
							{redactor.name[0]?.toUpperCase() ?? ''}
							{redactor.name[1]?.toUpperCase() ?? ''}
						</Avatar>
						<Typography.Text>{redactor.name}</Typography.Text>
					</AvatarCell>
				),
			},
			{
				title: 'Email',
				dataIndex: 'email',
				key: 'email',
			},
			{
				title: 'Status',
				dataIndex: 'isActive',
				key: 'isActive',
				render: (isActive) =>
					isActive ? <Tag color='green'>Ativo</Tag> : <Tag color='red'>Inativo</Tag>,
			},
			{
				title: 'Ações',
				key: 'actions',
				align: 'right',
				render: (redactor) => (
					<Dropdown
						placement='bottomLeft'
						overlay={
							<Menu>
								<Menu.Item
									key='edit'
									onClick={() => {
										setHandlingRedactor(redactor);
										setVisibleModals((prev) => ({ ...prev, edit: true }));
									}}>
									<Icon type='edit' /> Editar
								</Menu.Item>

								<Menu.Item
									key='details'
									onClick={() => {
										setHandlingRedactor(redactor);
										setVisibleModals((prev) => ({ ...prev, changePassword: true }));
									}}>
									<Icon type='lock' /> Alterar Senha
								</Menu.Item>

								<Menu.Item
									key='edit-radios'
									onClick={() => {
										setHandlingRedactor(redactor);
										setVisibleModals((prev) => ({ ...prev, updateRadios: true }));
									}}>
									<Icon type='edit' /> Alterar Acesso à Rádios
								</Menu.Item>

								<Menu.Divider />

								<Menu.Item
									key='delete'
									className='ant-dropdown-menu-item-danger'
									onClick={() => {
										Modal.confirm({
											title: 'Excluir jornalista?',
											icon: 'exclamation-circle',
											content:
												'Ao continuar o jornalista e todos os arquivos gerados por ele serão excluídos. Deseja continuar?',
											okText: 'Excluir',
											onOk: () => handleDeleteRedactor(redactor?._id),
											okButtonProps: {
												icon: 'delete',
												type: 'danger',
											},
											cancelText: 'Cancelar',
											cancelButtonProps: {
												icon: 'close-circle',
											},
										});
									}}>
									<Icon type='delete' /> Excluir
								</Menu.Item>
							</Menu>
						}>
						<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 12 }} type='more' />
					</Dropdown>
				),
			},
		];
	}, [handleDeleteRedactor]);

	const editAvatarSrc = useMemo(() => {
		if (avatarFile) {
			return URL.createObjectURL(avatarFile);
		}

		return handlingRedactor?.avatar
			? resolveFileSrc({
					fileName: handlingRedactor?.avatar,
					folder: ['profile_pics'],
					bucket: env.miscS3Bucket,
			  })
			: null;
	}, [avatarFile, handlingRedactor?.avatar]);

	const onSelectAvatarFile = useCallback((e) => {
		const file = e.target.files[0];
		const ext = file?.name ? file.name.split('.')[1] : null;

		if (!file || !ext) {
			return setAvatarFile(null);
		}

		if (!['jpg', 'jpeg', 'png'].includes(ext.toLowerCase())) {
			setAvatarFile(null);
			return message.error(
				'Fromato inválido, selecione um imagem em um dos seguitnes formatos: .png, .jpg ou .jpeg',
				4
			);
		}

		return setAvatarFile(file);
	}, []);

	const handleCreateRedactor = useCallback(async () => {
		try {
			const validationSchema = Yup.object().shape({
				name: Yup.string().required(),
				email: Yup.string().required(),
				hasOpenaiAccess: Yup.boolean(),
				password: Yup.string().required(),
				passwordConfirmation: Yup.string().required(),
			});

			const formData = await validationSchema.validate({
				...handlingRedactor,
				password,
				passwordConfirmation,
			});

			if (password !== passwordConfirmation) {
				return message.error('Senhas não são iguais');
			}

			const payload = new FormData();
			payload.append('email', formData.email);
			payload.append('name', formData.name);
			payload.append('hasOpenaiAccess', formData.hasOpenaiAccess || false);
			payload.append('password', formData.password);
			payload.append('passwordConfirmation', formData.passwordConfirmation);
			payload.append('isActive', true);

			selectedRadios.forEach((item) => {
				payload.append('radioIds[]', item);
			});

			if (avatarFile) {
				payload.append('avatar', avatarFile);
			}

			setFallback((prev) => ({ ...prev, creatingRedactor: true }));
			await RedactorsAPI.createRedactor(payload);
			message.success('Joranlista criado com sucesso');
			setVisibleModals((prev) => ({ ...prev, create: false }));
			setHandlingRedactor(null);
			setAvatarFile(null);
			setFallback((prev) => ({ ...prev, creatingRedactor: false }));
			setSelectedRadios([]);
			return fetchData({
				...pagination,
				filters,
			});
		} catch (error) {
			if (error instanceof Yup.ValidationError) {
				return message.info('Informe todos os campos obrigatórios');
			}

			message.error('Erro ao criar o jornalista, tente novamente');
		}

		setFallback((prev) => ({ ...prev, creatingRedactor: false }));
	}, [
		avatarFile,
		fetchData,
		filters,
		handlingRedactor,
		pagination,
		password,
		passwordConfirmation,
		selectedRadios,
	]);

	const handleEditRedactor = useCallback(async () => {
		const payload = new FormData();
		payload.append('email', handlingRedactor.email);
		payload.append('name', handlingRedactor.name);
		payload.append('hasOpenaiAccess', handlingRedactor.hasOpenaiAccess || false);
		payload.append('isActive', handlingRedactor.isActive);

		if (avatarFile) {
			payload.append('avatar', avatarFile);
		}

		setFallback((prev) => ({ ...prev, updatingInfos: true }));

		try {
			const redactorId = handlingRedactor._id;
			await RedactorsAPI.updateRedactor(redactorId, payload);
			message.success('Joranlista editado com sucesso');
			setVisibleModals((prev) => ({ ...prev, edit: false }));
			setHandlingRedactor(null);
			setAvatarFile(null);
			return fetchData({
				...pagination,
				filters,
			});
		} catch (error) {
			message.error('Erro ao editar o jornalista, tente novamente');
		}

		setFallback((prev) => ({ ...prev, updatingInfos: false }));
	}, [handlingRedactor, avatarFile, fetchData, pagination, filters]);

	const handleChangePassword = useCallback(async () => {
		if (password !== passwordConfirmation) {
			return message.error('Senhas não são iguais');
		}

		setFallback((prev) => ({ ...prev, updatingPassword: true }));

		try {
			await RedactorsAPI.updateRedactorPassword(handlingRedactor?._id, {
				password,
				passwordConfirmation,
			});

			message.success('Senha alterada com sucesso');
			setHandlingRedactor(null);
			setVisibleModals((prev) => ({ ...prev, changePassword: false }));
		} catch (error) {
			message.success('Houve um erro ao alterar a senha, tente novamente');
		}

		setFallback((prev) => ({ ...prev, updatingPassword: false }));
	}, [handlingRedactor, password, passwordConfirmation]);

	const getRadios = async () => {
		const { data } = await UsersAPI.index('active=true&pageNumber=0&pageSize=99999');
		const options = data.users.map((radio) => {
			return { value: radio._id, label: radio.radioName };
		});
		setRadios(options);
	};

	const getRedactorRadios = useCallback(async () => {
		const { data } = await RedactorsAPI.getRedactorRadios(handlingRedactor._id);
		if (data.length) {
			const radioIds = data.map((radio) => radio._id);
			setSelectedRadios(radioIds);
		}
	}, [handlingRedactor?._id]);

	const updateRadios = async () => {
		setSelectedRadios([]);
		await RedactorsAPI.updateRadios(handlingRedactor._id, { radioIds: selectedRadios });
		setVisibleModals((prev) => ({ ...prev, updateRadios: false }));
	};

	useEffect(() => {
		if (visibleModals.create) {
			getRadios();
		} else if (visibleModals.updateRadios) {
			getRadios();
			getRedactorRadios();
		}
	}, [getRedactorRadios, visibleModals]);

	useEffect(() => {
		fetchData({ pageNumber: pagination.currentPage });
	}, [fetchData, pagination.currentPage]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Jornalistas' />

			<input
				type='file'
				accept='.png, .jpg, .jpeg'
				style={{ display: 'none' }}
				onChange={onSelectAvatarFile}
				ref={uploadInputRef}
			/>

			<PageHeader
				title='Jornalistas'
				breadcrumb={breadcrumb}
				extra={[
					<Button
						key='new'
						type='primary'
						icon='plus-circle'
						onClick={() => {
							setHandlingRedactor({
								name: '',
								email: '',
							});
							setPassword('');
							setPasswordConfirmation('');
							setVisibleModals((prev) => ({ ...prev, create: true }));
						}}>
						Novo Jornalista
					</Button>,
				]}></PageHeader>

			<Container>
				<Card>
					<FiltersContainer onSubmit={onFilter}>
						<Form.Item label='Nome'>
							<Input
								placeholder='Buscar por nome'
								onChange={(e) => {
									e.persist();
									setFilters((prev) => ({ ...prev, name: e.target.value }));
								}}
							/>
						</Form.Item>

						<Form.Item label='Email'>
							<Input
								placeholder='Buscar por email'
								onChange={(e) => {
									e.persist();
									setFilters((prev) => ({ ...prev, email: e.target.value }));
								}}
							/>
						</Form.Item>

						<Form.Item label='Status'>
							<Select
								placeholder='Filtrar por status'
								onChange={(isActive) => {
									setFilters((prev) => ({ ...prev, isActive: JSON.parse(isActive) }));
								}}>
								<Select.Option value={'true'}>Ativos</Select.Option>
								<Select.Option value={'false'}>Inativos</Select.Option>
							</Select>
						</Form.Item>

						<Button htmlType='submit' type='primary' icon='search'>
							Filtrar
						</Button>
					</FiltersContainer>

					<Table
						loading={fallback?.fetchingRedactors}
						size='middle'
						rowKey='_id'
						columns={columns}
						dataSource={redactors}
						style={{ border: 'none' }}
						pagination={{
							...pagination,
							size: 'large',
							onChange: (currentPage) => setPagination({ ...pagination, currentPage }),
						}}
					/>
				</Card>
			</Container>

			<Modal
				title={
					<>
						<Icon type='lock' style={{ marginRight: 8 }} /> Alterar senha
					</>
				}
				visible={visibleModals.changePassword}
				okText='Salvar'
				onOk={handleChangePassword}
				okButtonProps={{ loading: fallback?.updatingPassword, icon: 'check-circle' }}
				cancelButtonProps={{ disabled: fallback?.updatingPassword, icon: 'close-circle' }}
				onCancel={() => {
					setHandlingRedactor(null);
					setVisibleModals((prev) => ({ ...prev, changePassword: false }));
				}}>
				<Form.Container>
					<Form.Item label='Senha'>
						<Input.Password
							size='large'
							placeholder='Informe a nova senha'
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
					</Form.Item>
					<Form.Item label='Confirmar senha'>
						<Input.Password
							size='large'
							placeholder='Confirme a nova senha'
							value={passwordConfirmation}
							onChange={(e) => setPasswordConfirmation(e.target.value)}
						/>
					</Form.Item>
				</Form.Container>
			</Modal>

			<Modal
				title='Editar Informações'
				okText='Salvar'
				visible={visibleModals.edit}
				onOk={handleEditRedactor}
				okButtonProps={{ loading: fallback?.updatingInfos, icon: 'check-circle' }}
				cancelButtonProps={{ disabled: fallback?.updatingInfos, icon: 'close-circle' }}
				onCancel={() => {
					setHandlingRedactor(null);
					setVisibleModals((prev) => ({ ...prev, edit: false }));
				}}>
				<UpdateFormContainer>
					<UpdateAvatarContainer>
						<div className='overlay' onClick={() => uploadInputRef.current.click()}>
							<span>
								<TbEdit style={{ fontSize: 40 }} />
							</span>
						</div>
						<Avatar
							style={{ width: '120px', height: '120px', background: 'var(--primary)' }}
							src={editAvatarSrc}>
							{handlingRedactor?.name
								? `${handlingRedactor?.name[0]?.toUpperCase() ?? ''}${
										handlingRedactor?.name[1]?.toUpperCase() ?? ''
								  }`
								: '-'}

							{}
						</Avatar>
					</UpdateAvatarContainer>

					<Form.Container>
						<Form.Item label='Nome'>
							<Input
								size='large'
								placeholder='Informe o nome'
								value={handlingRedactor?.name}
								onChange={(e) => setHandlingRedactor((prev) => ({ ...prev, name: e.target.value }))}
							/>
						</Form.Item>

						<Form.Item label='Email'>
							<Input
								size='large'
								placeholder='Informe o email'
								value={handlingRedactor?.email}
								onChange={(e) =>
									setHandlingRedactor((prev) => ({ ...prev, email: e.target.value }))
								}
							/>
						</Form.Item>

						<Form.Item label='Acesso à API da OpenAI'>
							<div>
								<Switch
									size='default'
									style={{ width: 'max-content' }}
									checked={handlingRedactor?.hasOpenaiAccess}
									onChange={(hasOpenaiAccess) =>
										setHandlingRedactor((prev) => ({ ...prev, hasOpenaiAccess }))
									}
								/>
							</div>
						</Form.Item>

						<Form.Item label='Ativo'>
							<div>
								<Switch
									size='default'
									style={{ width: 'max-content' }}
									checked={handlingRedactor?.isActive}
									onChange={(isActive) => setHandlingRedactor((prev) => ({ ...prev, isActive }))}
								/>
							</div>
						</Form.Item>
					</Form.Container>
				</UpdateFormContainer>
			</Modal>

			<Modal
				title='Novo Jornalista'
				okText='Salvar'
				visible={visibleModals.create}
				onOk={handleCreateRedactor}
				okButtonProps={{ loading: fallback?.creatingRedactor, icon: 'check-circle' }}
				cancelButtonProps={{ disabled: fallback?.creatingRedactor, icon: 'close-circle' }}
				onCancel={() => {
					setHandlingRedactor(null);
					setVisibleModals((prev) => ({ ...prev, create: false }));
					setSelectedRadios([]);
				}}>
				<UpdateFormContainer>
					<UpdateAvatarContainer>
						<div className='overlay' onClick={() => uploadInputRef.current.click()}>
							<span>
								<TbEdit style={{ fontSize: 40 }} />
							</span>
						</div>
						<Avatar
							style={{ width: '120px', height: '120px', background: 'var(--primary)' }}
							src={editAvatarSrc}>
							-
						</Avatar>
					</UpdateAvatarContainer>

					<Form.Container>
						<Form.Item label='Nome'>
							<Input
								size='large'
								placeholder='Informe o nome'
								value={handlingRedactor?.name ?? ''}
								onChange={(e) => {
									e.persist();
									setHandlingRedactor((prev) => ({ ...prev, name: e.target.value }));
								}}
							/>
						</Form.Item>

						<Form.Item label='Email'>
							<Input
								size='large'
								placeholder='Informe o email'
								value={handlingRedactor?.email ?? ''}
								onChange={(e) => {
									e.persist();
									setHandlingRedactor((prev) => ({ ...prev, email: e.target?.value }));
								}}
							/>
						</Form.Item>

						<Form.Item label='Acesso à API da OpenAI'>
							<div>
								<Switch
									size='default'
									style={{ width: 'max-content' }}
									checked={handlingRedactor?.hasOpenaiAccess}
									onChange={(hasOpenaiAccess) =>
										setHandlingRedactor((prev) => ({ ...prev, hasOpenaiAccess }))
									}
								/>
							</div>
						</Form.Item>

						<Form.Item label='Rádios'>
							<div
								style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginBottom: 8 }}>
								<Button
									size='small'
									type='default'
									onClick={() => setSelectedRadios(radios.map((r) => r.value))}>
									Selecionar todos
								</Button>
								<Button size='small' danger onClick={() => setSelectedRadios([])}>
									Limpar seleção
								</Button>
							</div>
							<Select
								mode='multiple'
								allowClear
								showSearch
								style={{ width: '100%' }}
								placeholder='Digite para buscar ou selecione'
								onChange={setSelectedRadios}
								value={selectedRadios}
								loading={radios.length === 0}
								notFoundContent={radios.length === 0 ? 'Carregando...' : 'Nenhuma opção encontrada'}
								filterOption={(input, option) =>
									option?.props?.children?.toLowerCase()?.includes(input.toLowerCase()) ?? false
								}>
								{radios.map((radio) => (
									<Select.Option key={radio.value} value={radio.value}>
										{radio.label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item label='Senha'>
							<Input.Password
								size='large'
								placeholder='Informe a nova senha'
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
						</Form.Item>
						<Form.Item label='Confirmar senha'>
							<Input.Password
								size='large'
								placeholder='Confirme a nova senha'
								value={passwordConfirmation}
								onChange={(e) => setPasswordConfirmation(e.target.value)}
							/>
						</Form.Item>
					</Form.Container>
				</UpdateFormContainer>
			</Modal>

			<Modal
				title='Editar Acesso à Rádios'
				okText='Salvar'
				visible={visibleModals.updateRadios}
				onOk={updateRadios}
				okButtonProps={{ loading: fallback?.updtatingRadios, icon: 'check-circle' }}
				cancelButtonProps={{ disabled: fallback?.updtatingRadios, icon: 'close-circle' }}
				onCancel={() => {
					setHandlingRedactor(null);
					setVisibleModals((prev) => ({ ...prev, updateRadios: false }));
					setSelectedRadios([]);
				}}>
				<UpdateFormContainer>
					<Form.Container>
						<Form.Item label='Rádios'>
							<div
								style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginBottom: 8 }}>
								<Button
									size='small'
									type='default'
									onClick={() => setSelectedRadios(radios.map((r) => r.value))}>
									Selecionar todos
								</Button>
								<Button size='small' danger onClick={() => setSelectedRadios([])}>
									Limpar seleção
								</Button>
							</div>
							<Select
								mode='multiple'
								allowClear
								showSearch
								style={{ width: '100%' }}
								placeholder='Digite para buscar ou selecione'
								onChange={setSelectedRadios}
								value={selectedRadios}
								loading={radios.length === 0}
								notFoundContent={radios.length === 0 ? 'Carregando...' : 'Nenhuma opção encontrada'}
								filterOption={(input, option) =>
									option?.props?.children?.toLowerCase()?.includes(input.toLowerCase()) ?? false
								}>
								{radios.map((radio) => (
									<Select.Option key={radio.value} value={radio.value}>
										{radio.label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Form.Container>
				</UpdateFormContainer>
			</Modal>
		</>
	);
};

export default ListRedactors;
